import { c as _c } from "react/compiler-runtime";
import { ChevronDown } from "lucide-react";
import { type KeyboardEvent, useEffect, useRef, useState } from "react";
import type { RefCallBack } from "react-hook-form";
import { useIntl } from "react-intl";
import { SelectItem } from "ui/Select/SelectItem";
import { useFormField } from "uiShadcn/form";
import { Popover, PopoverContent, PopoverTrigger } from "uiShadcn/popover";
import capitalize from "utils/helpers/captizalize";
import { cn } from "utils/helpers/cn";
export interface SelectProps {
  options: {
    key: string;
    value: string;
  }[];
  onValueChange: (value: string) => void;
  defaultValue: string;
  dataTestId: string;
  placeholder?: string;
  className?: string;
  /**
   * The modality of the popover. When set to true, interaction with outside elements
   * will be disabled and only popover content will be visible to screen readers.
   * Optional, defaults to false.
   */
  modalPopover?: boolean;
  required?: boolean;
  ref?: RefCallBack;
  ariaDescribedBy?: string;
}
export const Select = t0 => {
  const $ = _c(40);
  const {
    onValueChange,
    defaultValue: t1,
    placeholder,
    modalPopover: t2,
    className,
    dataTestId,
    required: t3,
    ref,
    options,
    ariaDescribedBy
  } = t0;
  const defaultValue = t1 === undefined ? "" : t1;
  const modalPopover = t2 === undefined ? false : t2;
  const required = t3 === undefined ? false : t3;
  const {
    error,
    formItemId
  } = useFormField();
  const {
    formatMessage
  } = useIntl();
  const [selectedValue, setSelectedValue] = useState(defaultValue);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(0);
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const optionRefs = useRef(t4);
  const triggerRef = useRef(null);
  const defaultOptions = [{
    key: "null",
    value: formatMessage({
      id: "select"
    })
  }];
  const parsedOptions = options.map(_temp);
  const selectOptions = [...defaultOptions, ...parsedOptions];
  const selectOptionsLength = selectOptions?.length;
  let t5;
  if ($[1] !== onValueChange) {
    t5 = key => {
      setSelectedValue(key);
      onValueChange(key);
      setIsPopoverOpen(false);
      setFocusedIndex(0);
    };
    $[1] = onValueChange;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const selectOption = t5;
  const handleKeyDown = event => {
    const {
      key: key_0
    } = event;
    if (key_0 === "ArrowDown" || key_0 === "ArrowRight") {
      event.preventDefault();
      setFocusedIndex(prev => prev === null ? 0 : (prev + 1) % selectOptionsLength);
    } else {
      if (key_0 === "ArrowUp" || key_0 === "ArrowLeft") {
        event.preventDefault();
        setFocusedIndex(prev_0 => prev_0 === null ? selectOptionsLength - 1 : (prev_0 - 1 + selectOptionsLength) % selectOptionsLength);
      } else {
        if (key_0 === "Enter" || key_0 === " ") {
          event.preventDefault();
          selectOption(selectOptions[focusedIndex ?? 0].key);
          setFocusedIndex(0);
        }
      }
    }
  };
  let t6;
  let t7;
  if ($[3] !== focusedIndex || $[4] !== isPopoverOpen) {
    t6 = () => {
      const time = setTimeout(() => {
        if (isPopoverOpen) {
          optionRefs.current[focusedIndex ?? 0]?.focus();
        }
      }, 0);
      return () => clearTimeout(time);
    };
    t7 = [focusedIndex, isPopoverOpen];
    $[3] = focusedIndex;
    $[4] = isPopoverOpen;
    $[5] = t6;
    $[6] = t7;
  } else {
    t6 = $[5];
    t7 = $[6];
  }
  useEffect(t6, t7);
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = open => {
      setIsPopoverOpen(open);
      if (!open) {
        triggerRef.current?.focus();
      }
    };
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const handleOpenChange = t8;
  const T0 = Popover;
  const T1 = PopoverTrigger;
  const t9 = true;
  const t10 = "button";
  let t11;
  if ($[8] !== ref) {
    t11 = el => {
      if (ref) {
        ref(el);
      }
      triggerRef.current = el;
    };
    $[8] = ref;
    $[9] = t11;
  } else {
    t11 = $[9];
  }
  const t12 = !!error;
  let t13;
  if ($[10] !== className || $[11] !== t12) {
    t13 = cn("flex items-center justify-between rounded-md border border-input bg-background px-n3 py-n4", "text-font-2 text-sm ring-offset-background", "focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2", {
      "border-destructive focus:ring-destructive": t12
    }, className);
    $[10] = className;
    $[11] = t12;
    $[12] = t13;
  } else {
    t13 = $[12];
  }
  const t14 = required && !selectedValue;
  const t15 = selectedValue ? <p className="flex items-center gap-2 dark:text-foreground">{capitalize(selectOptions.find(o => o.key === selectedValue)?.value || "")}</p> : <span className="text-muted-foreground">{placeholder}</span>;
  let t16;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <ChevronDown className="h-5 w-5" />;
    $[13] = t16;
  } else {
    t16 = $[13];
  }
  let t17;
  if ($[14] !== ariaDescribedBy || $[15] !== dataTestId || $[16] !== formItemId || $[17] !== required || $[18] !== t11 || $[19] !== t13 || $[20] !== t14 || $[21] !== t15) {
    t17 = <button type={t10} ref={t11} className={t13} data-test-id={dataTestId} aria-required={required} aria-invalid={t14} aria-describedby={ariaDescribedBy} id={formItemId}>{t15}{t16}</button>;
    $[14] = ariaDescribedBy;
    $[15] = dataTestId;
    $[16] = formItemId;
    $[17] = required;
    $[18] = t11;
    $[19] = t13;
    $[20] = t14;
    $[21] = t15;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  let t18;
  if ($[23] !== T1 || $[24] !== t17) {
    t18 = <T1 asChild={t9}>{t17}</T1>;
    $[23] = T1;
    $[24] = t17;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  let t19;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => setIsPopoverOpen(false);
    $[26] = t19;
  } else {
    t19 = $[26];
  }
  const t20 = focusedIndex !== null ? `option-${selectOptions[focusedIndex]?.key}` : undefined;
  const t21 = selectOptions.map((option_0, index) => <SelectItem id={`option-${option_0.key}`} key={option_0.key} value={option_0.value} isSelected={selectedValue === option_0.key} onClick={() => selectOption(option_0.key)} dataTestId={`${dataTestId}-option-${option_0.key}`} ref={el_0 => {
    optionRefs.current[index] = el_0;
  }} />);
  let t22;
  if ($[27] !== t20 || $[28] !== t21) {
    t22 = <ul className="flex flex-col gap-n1 p-n1" role="listbox" aria-activedescendant={t20} tabIndex={-1}>{t21}</ul>;
    $[27] = t20;
    $[28] = t21;
    $[29] = t22;
  } else {
    t22 = $[29];
  }
  let t23;
  if ($[30] !== handleKeyDown || $[31] !== t22) {
    t23 = <PopoverContent className="max-h-[var(--radix-popover-content-available-height)] w-[var(--radix-popover-trigger-width)] overflow-auto bg-background p-0" onEscapeKeyDown={t19} onKeyDown={handleKeyDown}>{t22}</PopoverContent>;
    $[30] = handleKeyDown;
    $[31] = t22;
    $[32] = t23;
  } else {
    t23 = $[32];
  }
  let t24;
  if ($[33] !== T0 || $[34] !== handleOpenChange || $[35] !== isPopoverOpen || $[36] !== modalPopover || $[37] !== t18 || $[38] !== t23) {
    t24 = <T0 open={isPopoverOpen} onOpenChange={handleOpenChange} modal={modalPopover}>{t18}{t23}</T0>;
    $[33] = T0;
    $[34] = handleOpenChange;
    $[35] = isPopoverOpen;
    $[36] = modalPopover;
    $[37] = t18;
    $[38] = t23;
    $[39] = t24;
  } else {
    t24 = $[39];
  }
  return t24;
};
Select.displayName = "Select";
function _temp(option) {
  return {
    ...option,
    key: option.key.toString()
  };
}